/**
 * Value shape of the saved-filter 'between' operator (SqlViewFilterBuilder).
 *
 * The API reads a between value with SqlViewQueryService.ParseArrayValue and requires exactly two
 * values. The wizard writes `[from, to]` - the same array shape the dashboard's date-zoom filter
 * already sends (DashboardItem) - while configs saved before the two-input editor may hold an
 * `'a,b'` string or a JSON-array string. Reading accepts all three; a value is only rewritten when
 * the user edits it, so an untouched legacy value (and the sourceKey hashed from it) is unchanged.
 *
 * PURE: no Vue, no store, no I/O.
 */

export const BETWEEN_OPERATOR = 'between';

function part(value) {
  return value === null || value === undefined ? '' : String(value).trim();
}

/**
 * The `[from, to]` pair of a saved between value, as strings ('' for a missing end). Accepts an
 * array, an 'a,b' string, a JSON-array string, a single value or nothing.
 * @param {*} value
 * @returns {[string, string]}
 */
export function parseBetweenValue(value) {
  if (Array.isArray(value)) return [part(value[0]), part(value[1])];
  const text = part(value);
  if (!text) return ['', ''];
  if (text.startsWith('[')) {
    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) return [part(parsed[0]), part(parsed[1])];
    } catch (err) {
      // Not JSON: fall through to the comma split, as the server does.
    }
  }
  const [from, to] = text.split(',');
  return [part(from), part(to)];
}

/**
 * A new between value with one end replaced (0 = from, 1 = to). The current value is not modified.
 * @param {*} value - the current saved value, in any shape parseBetweenValue reads
 * @param {number} index - 0 for from, 1 for to
 * @param {*} next - the new value of that end
 * @returns {[string, string]}
 */
export function withBetweenPart(value, index, next) {
  const [from, to] = parseBetweenValue(value);
  return index === 0 ? [part(next), to] : [from, part(next)];
}

/**
 * The value a filter should hold after its operator changes: a `[from, to]` pair for between
 * (keeping a single value as the from end), a single value for every other operator (keeping the
 * from end of a former range).
 * @param {string} operator - the new operator
 * @param {*} value - the current value
 * @returns {*}
 */
export function filterValueForOperator(operator, value) {
  if (operator === BETWEEN_OPERATOR) return parseBetweenValue(value);
  if (Array.isArray(value)) return part(value[0]);
  return value;
}

/**
 * A message for the first between filter that lacks either end, or null when every between filter
 * is complete. The server only counts values, so a blank end would otherwise save and query as
 * BETWEEN 10 AND '' (0 or 1900-01-01 after SQL conversion).
 * @param {Array<{ column?: string, operator?: string, value?: * }>} filters
 * @returns {string|null}
 */
export function betweenFilterError(filters) {
  const incomplete = (filters || []).find(filter => filter && filter.column
    && filter.operator === BETWEEN_OPERATOR
    && parseBetweenValue(filter.value).some(end => end === ''));
  return incomplete ? `Set both ends of the 'between' filter on ${incomplete.column}.` : null;
}
